import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["title"]
const _hoisted_2 = { class: "app-notification__icon-wrapper" }
const _hoisted_3 = {
  key: 0,
  class: "app-notification__pulse"
}
const _hoisted_4 = {
  key: 0,
  class: "app-notification__badge"
}
const _hoisted_5 = {
  key: 0,
  class: "app-notification__panel-wrapper"
}

import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from '@/composables/useStore'
import { useI18n } from 'vue-i18n'
import type { UserNotificationDTO } from '@/types'
import NotificationPanel from './NotificationPanel.vue'
import OtpVerificationDialog from './OtpVerificationDialog.vue'
import userInvitationsService from '@/services/userInvitations.service'
import userNotificationService from '@/services/userNotification.service'

// Define interfaces
interface NotificationItem {
  id: string | number
  type: string
  title: string
  message: string
  timestamp: Date
  read: boolean
  data?: any
  actionUrl?: string
  projectId?: number
  referenceId?: number
}

interface InvitationData {
  id: string | number
  projectId: string
  projectName: string
  inviterName: string
  inviterEmail: string
  accessRights: string[]
  invitationDate: string
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED'
  expirationDate?: string
}

// Store and i18n

export default /*@__PURE__*/_defineComponent({
  __name: 'NotificationIcon',
  setup(__props) {

/// <reference types="vue" />
// @ts-ignore
const router = useRouter()
const store = useStore()
const { t } = useI18n()

// Reactive data
const notificationRef = ref<HTMLElement | null>(null)
const panelVisible = ref(false)
const notifications = ref<NotificationItem[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// OTP Dialog state
const showOtpDialog = ref(false)
const selectedNotification = ref<NotificationItem | null>(null)
const otpLoading = ref(false)

// Computed properties
const notificationCount = computed((): number => {
  return notifications.value.filter((n: NotificationItem) => !n.read).length
})

// Extract UserNotificationDTO items from notifications for the panel
const userNotificationsList = computed((): UserNotificationDTO[] => {
  return notifications.value
    .filter((n: NotificationItem) => n.data && typeof n.data === 'object' && 'contextData' in n.data)
    .map((n: NotificationItem) => n.data as UserNotificationDTO)
})

// Methods
const toggleNotificationPanel = (): void => {
  panelVisible.value = !panelVisible.value
  if (panelVisible.value) {
    fetchNotifications()
    document.addEventListener('click', handleClickOutside)
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
}

const closeNotificationPanel = (): void => {
  panelVisible.value = false
  document.removeEventListener('click', handleClickOutside)
}

const handleClickOutside = (event: Event): void => {
  if (notificationRef.value && !notificationRef.value.contains(event.target as Node)) {
    closeNotificationPanel()
  }
}

const fetchNotifications = async (): Promise<void> => {
  loading.value = true
  error.value = null

  try {
    const allNotifications: NotificationItem[] = []
    
    // Only fetch project invitations for CLIENT users
    const currentUser = store.getters.getUser
    const shouldFetchInvitations = currentUser?.type === 'CLIENT'
    
    // Fetch project invitations (only for CLIENT users) and user notifications
    const [invitationsResponse, userNotifications] = await Promise.all([
      shouldFetchInvitations ? userInvitationsService.getPendingInvitations() : Promise.resolve(null),
      userNotificationService.getNotifications()
    ])

    // Process project invitations (only if fetched)
    if (invitationsResponse && shouldFetchInvitations) {
      let invitations: InvitationData[] = []
      if (Array.isArray(invitationsResponse)) {
        invitations = invitationsResponse as unknown as InvitationData[]
      } else if ((invitationsResponse as any).invitations && Array.isArray((invitationsResponse as any).invitations)) {
        invitations = (invitationsResponse as any).invitations as InvitationData[]
      } else if ((invitationsResponse as any).data && Array.isArray((invitationsResponse as any).data)) {
        invitations = (invitationsResponse as any).data as InvitationData[]
      }

      // Transform invitations into notification format
      const invitationNotifications = invitations.map((invitation: InvitationData): NotificationItem => ({
        id: invitation.id,
        type: 'project_invitation',
        title: t('notifications.projectInvitation'),
        message: t('notifications.invitedToProject', {
          projectName: invitation.projectName,
          firmName: invitation.inviterName
        }),
        timestamp: new Date(invitation.invitationDate),
        read: false,
        data: invitation
      }))

      allNotifications.push(...invitationNotifications)
    }

    // Process user notifications
    if (userNotifications && Array.isArray(userNotifications)) {
      const userNotifs = userNotifications.map((notif): NotificationItem => ({
        id: notif.id,
        type: notif.type,
        title: notif.type, // Use type as placeholder, will be translated in NotificationPanel
        message: notif.type, // Use type as placeholder, will be translated in NotificationPanel
        timestamp: new Date(notif.createdAt),
        read: notif.read,
        actionUrl: undefined, // Will be computed in NotificationPanel
        projectId: notif.projectId,
        referenceId: notif.referenceId,
        data: notif // IMPORTANT: Keep the original notification with contextData
      }))

      allNotifications.push(...userNotifs)
    }

    // Sort all notifications by timestamp (newest first)
    allNotifications.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())

    notifications.value = allNotifications

  } catch (err: any) {
    console.error('Error fetching notifications:', err)
    error.value = t('notifications.loadError')
  } finally {
    loading.value = false
  }
}

const handleShowOtpDialog = (notification: NotificationItem): void => {
  selectedNotification.value = notification
  showOtpDialog.value = true
  // Close the notification panel
  closeNotificationPanel()
}

const handleCloseOtpDialog = (): void => {
  showOtpDialog.value = false
  selectedNotification.value = null
  otpLoading.value = false
}

const handleOtpVerification = async (data: { notification: NotificationItem; otp: string }): Promise<void> => {
  otpLoading.value = true

  try {
    const invitation = data.notification.data

    await userInvitationsService.acceptInvitation(invitation.id, data.otp)

    // Remove notification from list
    notifications.value = notifications.value.filter((n: NotificationItem) => n.id !== data.notification.id)

    // Close OTP dialog
    handleCloseOtpDialog()

    // Show success message
    store.commit('SET_SUCCESS', t('notifications.invitationAccepted'))

    // Optionally refresh user data
    store.dispatch('fetchUser')

  } catch (err: any) {
    console.error('Error accepting invitation with OTP:', err)

    let errorMessage = t('notifications.acceptError')
    if (err.response?.data?.message) {
      errorMessage = err.response.data.message
    } else if (err.message) {
      errorMessage = err.message
    }

    store.commit('SET_ERROR', errorMessage)
  } finally {
    otpLoading.value = false
  }
}

const handleDeclineInvitation = async (notification: NotificationItem): Promise<void> => {
  try {
    const invitation = notification.data
    await userInvitationsService.declineInvitation(invitation.id)

    // Remove notification from list
    notifications.value = notifications.value.filter((n: NotificationItem) => n.id !== notification.id)

    // Show success message
    store.commit('SET_SUCCESS', t('notifications.invitationDeclined'))

  } catch (err: any) {
    console.error('Error declining invitation:', err)
    store.commit('SET_ERROR', t('notifications.declineError'))
  }
}

const handleNavigateToAction = async (notification: NotificationItem): Promise<void> => {
  try {
    // Navigate to the action URL
    if (notification.actionUrl) {
      await router.push(notification.actionUrl)
      // Close the notification panel
      closeNotificationPanel()
      
      // Mark as read
      if (!notification.read) {
        await userNotificationService.markAsRead(notification.id as number)
        // Update local state
        const index = notifications.value.findIndex((n: NotificationItem) => n.id === notification.id)
        if (index !== -1) {
          notifications.value[index].read = true
        }
      }
    }
  } catch (err: any) {
    console.error('Error navigating to action:', err)
    store.commit('SET_ERROR', t('notifications.navigationError'))
  }
}

const handleMarkAsRead = async (notification: NotificationItem): Promise<void> => {
  try {
    if (!notification.read) {
      await userNotificationService.markAsRead(notification.id as number)
      
      // Update local state
      const index = notifications.value.findIndex((n: NotificationItem) => n.id === notification.id)
      if (index !== -1) {
        notifications.value[index].read = true
      }
      
      store.commit('SET_SUCCESS', t('notifications.markedAsRead'))
    }
  } catch (err: any) {
    console.error('Error marking notification as read:', err)
    store.commit('SET_ERROR', t('notifications.markReadError'))
  }
}

// Lifecycle hooks
onMounted(() => {
  fetchNotifications()
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "app-notification",
    ref_key: "notificationRef",
    ref: notificationRef
  }, [
    _createElementVNode("button", {
      onClick: toggleNotificationPanel,
      class: _normalizeClass(["app-notification__btn", { 'app-notification__btn--has-notifications': notificationCount.value > 0 }]),
      title: _ctx.$t('notifications.title')
    }, [
      _createElementVNode("div", _hoisted_2, [
        _cache[0] || (_cache[0] = _createElementVNode("svg", {
          class: "app-notification__icon",
          viewBox: "0 0 24 24",
          width: "22",
          height: "22"
        }, [
          _createElementVNode("path", {
            fill: "currentColor",
            d: "M12,2C13.1,2 14,2.9 14,4C14,4.1 14,4.2 13.9,4.3C16.3,5.2 18,7.5 18,10.1V15.5C18,16.8 18.6,18 19.6,18.7L20,19V20H4V19L4.4,18.7C5.4,18 6,16.8 6,15.5V10.1C6,7.5 7.7,5.2 10.1,4.3C10,4.2 10,4.1 10,4C10,2.9 10.9,2 12,2M10,21C10,22.1 10.9,23 12,23C13.1,23 14,22.1 14,21"
          })
        ], -1)),
        (notificationCount.value > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3))
          : _createCommentVNode("", true)
      ]),
      (notificationCount.value > 0)
        ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(notificationCount.value > 9 ? '9+' : notificationCount.value), 1))
        : _createCommentVNode("", true)
    ], 10, _hoisted_1),
    _createVNode(_Transition, { name: "app-notification-fade" }, {
      default: _withCtx(() => [
        (panelVisible.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "app-notification__backdrop",
              onClick: closeNotificationPanel
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_Transition, { name: "app-notification-slide" }, {
      default: _withCtx(() => [
        (panelVisible.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(NotificationPanel, {
                onClose: closeNotificationPanel,
                notifications: userNotificationsList.value,
                loading: loading.value,
                error: error.value,
                onRefresh: fetchNotifications
              }, null, 8, ["notifications", "loading", "error"])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(OtpVerificationDialog, {
      show: showOtpDialog.value,
      notification: selectedNotification.value,
      "is-loading": otpLoading.value,
      onClose: handleCloseOtpDialog,
      onVerify: handleOtpVerification
    }, null, 8, ["show", "notification", "is-loading"])
  ], 512))
}
}

})